import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { CheckBox, CheckStyles, NewModal, MainContentPaddingTypes, NewInput, NewSelect, WaitAnimation, SystemControlsInner} from "../..";
import { NewFieldStyles, MenuFloats, LocaleFarmDate } from "../../../Definitions";
import { useSectionOptions } from "../../_Components";
import { useSection } from "../../../Services";
import { convertMinutesToTime } from "../../FeedCurves/_FeedCurves/feedCurveSpreadingUtil";
import { useGetNextFeedingTimeInformation } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getNextFeedingCall";
import { useLfContext } from "./_Modals/lfPenContext";
import { useAppSelector } from "../../../Store";
import { selectSectionOptionBySystem } from "../../../Store/optionsHandlers/optionsCombinedReducer";
import { isEqual } from "lodash";

interface StartSection_Props {
  isModalOpen: boolean;
  onClose: () => void;
  focus_view?: boolean;
}

export default function StartSectionModal({isModalOpen, onClose, focus_view}: StartSection_Props) {
  const { t } = useTranslation();
  const { state: {systemId, selectedSection, selectedPens, farm, domain, connection }, dispatch } = useLfContext();
  useSectionOptions()
  const sectionOptions = useAppSelector((state) => selectSectionOptionBySystem(state,[systemId]), isEqual);
  const [loading, setLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;

  // #region | Data fetch
  const { call: getLfSectionsInfo, data: lfSectionsState, feedLocationMultiple, startNextFeedingByLocation } = useSection(connection);
  const sections = useMemo( () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId] );
  
  const { data: nextFeedingTimeInformation, getNextFeedingTimeInformation, loading: nextFeedingTimeInformationLoading } = useGetNextFeedingTimeInformation();
  const nextTimeFormatted = nextFeedingTimeInformation.next_time ? convertMinutesToTime(LocaleFarmDate.restrictTimeTo24Hours(nextFeedingTimeInformation.next_time)) : undefined;


  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Locations to feed
  useEffect(() => {
    if (!farm) return;

    getNextFeedingTimeInformation(farm);

    const interval = setInterval(() => {
      getNextFeedingTimeInformation(farm);
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [farm, getNextFeedingTimeInformation]);

  const locationsToFeed = useMemo(() => {
    if (!selectedPens || selectedPens.length === 0) return [];

    return selectedPens
    .filter((location) =>
      location.animals.some((animal) =>
        nextFeedingTimeInformation.animal_ids.includes(animal.animal_id)
      )
    )
    .map((location) => location)
    .filter((pen) => selectedPens?.includes(pen)) ?? [];
  }, [nextFeedingTimeInformation.animal_ids, selectedPens]);
  // #endregion

  // Set initial section, pens and selected pens
  useEffect(() => {
    if (!sections || sections.length === 0) return;
  
    const first = sections[0];
  
    if (!first?.section_locations) return;
  
    dispatch({
      selectedSection: first,
      selectedPens: first.section_locations,
    });
  }, [dispatch, sections]);

  // #region | Handle Start Selected Button
  const [staticFeeding, setStaticFeeding] = useState(false);
  const [staticFeedAmount, setStaticFeedAmount] = useState<number>(0);

  useEffect(() => {
    if (staticFeeding || !nextFeedingTimeInformation) {
      dispatch({ nextFeeding_LocationsToFeed: selectedPens ?? [] });
      return;
    }

    const matchingPens =
      selectedPens?.filter(sel =>
        locationsToFeed.some(loc => loc.location_id === sel.location_id)
      ) ?? [];
      
    dispatch({ nextFeeding_LocationsToFeed: matchingPens });
  }, [staticFeeding, selectedPens, locationsToFeed, nextFeedingTimeInformation, dispatch]);
  
  function handleTimeout(elementId: number) {
    setTimeout(() => {
      dispatch({ error: { Elementid: elementId, errorText: "" } });
    }, 2000);
  }

  // Static Feeding
  const handleStaticFeeding = async () => {
    abortPreviousOperation();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const results = await feedLocationMultiple(selectedPens?.map(loc => loc.location_id) ?? [], true, staticFeedAmount, undefined, abortController.signal);
    
    if (!abortControllerRef.current) return;
    
    const newLocationResults: { [key: number]: boolean } = {};

    let errorOccured: boolean = false;

    for (let i = 0; i < results.length; i++) {
      let result = results[i];
      newLocationResults[result.id] = result.success;
      if (!result.success) {
        if (!errorOccured) {
          errorOccured = true;
          dispatch({ error: { Elementid: 1, errorText: t("systemControls.general.feedingFailed", { defaultValue: "Feeding failed" }) } });
          handleTimeout(1);
        }
      }
    }

    if (!errorOccured) {
      dispatch({ error: { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) } });
      handleTimeout(2);
    }

    setLoading(false);
    dispatch({locationResults: newLocationResults})
    abortControllerRef.current = null;
  };

  // Next Feeding
  const handleStartNextFeeding = async () => {
    abortPreviousOperation();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const results = await startNextFeedingByLocation(locationsToFeed.map(loc => loc.location_id), undefined, abortController.signal);
    if (!abortControllerRef.current) return;
    
    const newLocationResults: { [key: number]: boolean } = {};
    let errorOccured: boolean = false;

    for (let i = 0; i < results.length; i++) {
      let result = results[i];
      newLocationResults[result.id] = result.success;
      if (!result.success) {
        if (!errorOccured) {
          errorOccured = true;
          dispatch({ error: { Elementid: 1, errorText: t("systemControls.general.feedingFailed", { defaultValue: "Feeding failed" }) } });
          handleTimeout(1);
        }
      }
    }

    if (!errorOccured) {
      dispatch({ error: { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) } });
      handleTimeout(2);
    }

    setLoading(false);
    dispatch({locationResults: newLocationResults})
    abortControllerRef.current = null;
  };

  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  function resetResultState() {
    abortPreviousOperation();
    setLoading(false);
    dispatch({locationResults: []})
  }
  // #endregion

  // Button Handler
  const handleOnClick = () => {
    resetResultState()

    if (staticFeeding) {
      handleStaticFeeding();
    } else {
      handleStartNextFeeding();
    }
  };

  if (!sections) return null;
  const currentIndex = sections.findIndex(
    (s) => s.section_id === selectedSection?.section_id
  );

  const _fontSize = {...(focus_view ? fonts.fontSize3 : fonts.fontSize4)}

  return (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      modalState={isModalOpen}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      // tabButtons={{
      //   contents: ["start", "calibrate", "empty"],
      //   onClick: (i) => {
      //     if (i === 0) { }
      //     if (i === 1) { }
      //     if (i === 2) { }
      //   },
      //   startEnhancers: [glyphs.Start({}), glyphs.Calibrate({}), glyphs.Empty({})],
      //   active: 0,
      //   disabled: 0
      // }}
      predefinedButtons={{
        onClose: () => { dispatch({selectedSection: sections[0]}); resetResultState(); setStaticFeedAmount(0); onClose() },
        onNext: () => { dispatch({selectedSection: sections[(currentIndex + 1) % sections.length]}); resetResultState() },
        onPrev: () => { dispatch({selectedSection: sections[(currentIndex - 1 + sections.length) % sections.length]}); resetResultState() },
      }}
      subHeader={selectedSection?.name}
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        headerContentHeight: focus_view ? "60px" : undefined,
        mainContentScroll: mobile ? true : false
      }}
      headerContent={selectedSection ? (
        <NewSelect
          options={sectionOptions ?? []}
          key={selectedSection.section_id}
          onChange={(i) => { resetResultState(); dispatch({selectedSection: sections.find(section => section.section_id === i?.id)})}}
          valueOverrides={{
            defaultValue: sectionOptions?.find(option => option.id === selectedSection.section_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: mobile ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      ) : (<></>)}
      mainContent={
        <SystemControlsInner
          focus_view={focus_view}
          selectContent={
            <div style={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start" }}>
              <SelectFeedingType
                focus_view={focus_view}
                isChecked={!staticFeeding}
                onChange={() => setStaticFeeding(!staticFeeding)}
                text={t("systemControls.general.nextFeeding", { defaultValue: "Next Feeding" })}
                content={!staticFeeding ? (
                  nextFeedingTimeInformationLoading || !nextFeedingTimeInformation.next_time ? <WaitAnimation />
                    : <p style={{ ...fonts.paragraph, ..._fontSize, color: colors.greyDarkPrimary }}> Kl. {nextTimeFormatted} </p>
                ) : (<></>)}
              />
              <SelectFeedingType
                focus_view={focus_view}
                isChecked={staticFeeding}
                onChange={() => setStaticFeeding(!staticFeeding)}
                text={t("systemControls.general.staticFeeding", { defaultValue: "Static Feeding" })}
                content={staticFeeding ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <NewInput
                      placeholder={t("units.g", { defaultValue: "g" })}
                      onChange={(e) => setStaticFeedAmount(Number(e))}
                      fieldOverrides={{
                        fieldStyle: NewFieldStyles.greyTertiary,
                        fieldWidth: "100px",
                        focus_view: focus_view
                      }}
                      valueOverrides={{
                        valueType: "number",
                        minValueNumber: 0
                      }}
                    />
                    <p style={{ ...fonts.paragraph, ..._fontSize, color: colors.greyDarkPrimary }}> {t("units.g", { defaultValue: "G" })} </p>
                  </div>
                ) : (<></>)}
              />
            </div>
          }
          button={{
            content: t("systemControls.general.startSelected", { defaultValue: "Start Selected" }),
            startEnhancer: "Start",
            onClick: handleOnClick,
            disabled: loading || (!staticFeeding && locationsToFeed.length === 0)
          }}
        />
      }
    />
  );
}

const SelectFeedingType = memo(({focus_view, onChange, isChecked, text, content}:{focus_view?:boolean, onChange:()=>void, isChecked:boolean, text:string, content:JSX.Element}) => {
  return (
    <div style={{ height: focus_view ? "40px" : "30px", display: "flex", alignItems: "center", gap: "20px"}}>
      <CheckBox
        focus_view={focus_view}
        checkStyle={CheckStyles.grey}
        onChange={onChange}
        isChecked={isChecked}
        text={text}
      />
        {content}
    </div>
  )
})